import { GetProfileResponse } from '@features/user/types/get-profile-response';
import { api, markSessionDead } from '@src/lib/api-client';
import { QueryConfig } from '@src/lib/react-query';
import { useSessionStore } from '@src/stores/use-session-store';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { ToastUtility } from '@utils';
import { AxiosResponse } from 'axios';

const { restartSessionTimeout, rememberUser } =
  useSessionStore.getState().actions;

export async function getProfile(): Promise<GetProfileResponse> {
  return await api
    .get('/api/v3/user/profile')
    .then((res: AxiosResponse<GetProfileResponse>) => {
      // The profile is refetched on wizard unmount, after submit and once
      // stale, so a different User here means someone else signed in from
      // another tab. End the session before their profile reaches the cache,
      // and before restartSessionTimeout would keep it alive.
      if (!rememberUser(res.data.uuid)) {
        return markSessionDead('user_changed');
      }
      restartSessionTimeout(res.data.timeoutIn);
      return res.data;
    });
}

export const getProfileQueryOptions = queryOptions({
  queryKey: ['profile'],
  queryFn: getProfile,
});

type UseGetProfileOptions = {
  queryConfig?: QueryConfig<typeof getProfileQueryOptions>;
};

export const useGetProfile = ({ queryConfig }: UseGetProfileOptions = {}) => {
  return useSuspenseQuery({
    ...getProfileQueryOptions,
    ...queryConfig,
  });
};

export const callGetProfileErrorToast = () =>
  ToastUtility.error('Failed to fetch user profile', {
    toastId: 'profile',
  });
